// ─────────────────────────────────────────────────────────────────────────────
// Page header slice
// ─────────────────────────────────────────────────────────────────────────────
//
// Lets a page set its own contextual header in the global app bar — currently
// used by the rule builder to surface the rule's name + status in place of
// the generic "Rule Builder" text. Pages clear the context on unmount.
// ─────────────────────────────────────────────────────────────────────────────

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface PageHeaderBreadcrumb {
	label: string;
	href?: string;
}

export interface PageHeaderStatusBadge {
	label: string;
	tone: "active" | "inactive" | "draft" | "archived";
}

export interface PageHeaderState {
	title: string | null;
	breadcrumbs: PageHeaderBreadcrumb[];
	statusBadge: PageHeaderStatusBadge | null;
}

const initialState: PageHeaderState = {
	title: null,
	breadcrumbs: [],
	statusBadge: null
};

interface SetPageHeaderPayload {
	title: string;
	breadcrumbs?: PageHeaderBreadcrumb[];
	statusBadge?: PageHeaderStatusBadge | null;
}

export const pageHeaderSlice = createSlice({
	name: "pageHeader",
	initialState,
	reducers: {
		setPageHeader: (state, action: PayloadAction<SetPageHeaderPayload>) => {
			state.title = action.payload.title;
			state.breadcrumbs = action.payload.breadcrumbs ?? [];
			state.statusBadge = action.payload.statusBadge ?? null;
		},
		clearPageHeader: () => initialState
	}
});

interface RootState {
	pageHeader?: PageHeaderState;
}

export const selectPageHeader = (state: RootState): PageHeaderState =>
	state.pageHeader ?? initialState;

export const { setPageHeader, clearPageHeader } = pageHeaderSlice.actions;

export default pageHeaderSlice.reducer;
